import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  FileText,
  Database,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import ConflictResolutionModal from '@/components/ConflictResolutionModal';

interface SyncSession {
  id: string;
  connectorId: string;
  type: 'pull' | 'push' | 'reconciliation' | 'manual';
  status: 'running' | 'completed' | 'failed' | 'partial';
  startedAt: string;
  completedAt?: string;
  entitiesProcessed: number;
  entitiesCreated: number;
  entitiesUpdated: number;
  entitiesSkipped: number;
  errors: any[];
  warnings: string[];
}

interface Conflict {
  id: string;
  entityType: string;
  entityId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'resolved' | 'ignored';
  detectedAt: string;
}

interface QueueStats {
  pending: number;
  retrying: number;
  failed: number;
  completed: number;
}

const SyncIntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'sessions' | 'conflicts' | 'queue'>('overview');
  const [selectedConflict, setSelectedConflict] = useState<Conflict | null>(null);
  const queryClient = useQueryClient();

  // Mock data - replace with real API calls
  const { data: sessions = [], isLoading: isLoadingSessions } = useQuery<SyncSession[]>({
    queryKey: ['sync', 'sessions'],
    queryFn: async () => {
      // Mock data for now
      return [
        {
          id: '1',
          connectorId: 'file-1',
          type: 'pull' as const,
          status: 'completed' as const,
          startedAt: new Date(Date.now() - 3600000).toISOString(),
          completedAt: new Date(Date.now() - 3000000).toISOString(),
          entitiesProcessed: 150,
          entitiesCreated: 10,
          entitiesUpdated: 140,
          entitiesSkipped: 0,
          errors: [],
          warnings: [],
        },
        {
          id: '2',
          connectorId: 'file-2',
          type: 'pull' as const,
          status: 'failed' as const,
          startedAt: new Date(Date.now() - 7200000).toISOString(),
          entitiesProcessed: 0,
          entitiesCreated: 0,
          entitiesUpdated: 0,
          entitiesSkipped: 0,
          errors: [{ code: 'FILE_NOT_FOUND', message: 'Source file not found' }],
          warnings: [],
        },
      ];
    },
  });

  const { data: conflicts = [], isLoading: isLoadingConflicts } = useQuery<Conflict[]>({
    queryKey: ['sync', 'conflicts'],
    queryFn: async () => {
      // Mock data with more realistic structure
      return [
        {
          id: 'c1',
          entityType: 'product',
          entityId: 'prod-123',
          severity: 'medium' as const,
          status: 'open' as const,
          detectedAt: new Date(Date.now() - 1800000).toISOString(),
          sourceSystem: {
            data: { name: 'Product A', price: 10.99, sku: 'PROD-A' },
            lastModified: new Date(Date.now() - 3600000).toISOString(),
          },
          platform: {
            data: { name: 'Product A', price: 12.99, sku: 'PROD-A' },
            lastModified: new Date(Date.now() - 1800000).toISOString(),
          },
        },
        {
          id: 'c2',
          entityType: 'customer',
          entityId: 'cust-456',
          severity: 'high' as const,
          status: 'open' as const,
          detectedAt: new Date(Date.now() - 900000).toISOString(),
          sourceSystem: {
            data: { name: 'Customer B', email: 'customer@example.com' },
            lastModified: new Date(Date.now() - 1800000).toISOString(),
          },
          platform: {
            data: { name: 'Customer B', email: 'customer.b@example.com' },
            lastModified: new Date(Date.now() - 900000).toISOString(),
          },
        },
      ];
    },
  });

  const { data: queueStats = { pending: 5, retrying: 2, failed: 1, completed: 120 } } = useQuery<QueueStats>({
    queryKey: ['sync', 'queue'],
    queryFn: async () => {
      // Mock data
      return { pending: 5, retrying: 2, failed: 1, completed: 120 };
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleResolveConflict = (conflictId: string, resolution: string) => {
    // Mock resolution - in real app, this would call an API
    console.log('Resolving conflict', conflictId, 'with resolution', resolution);
    queryClient.invalidateQueries({ queryKey: ['sync', 'conflicts'] });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sync Intelligence</h1>
        <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['sync'] })}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'sessions', 'conflicts', 'queue'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sync Health</p>
                <p className="text-2xl font-bold text-gray-900">92%</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <Database className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Queue Depth</p>
                <p className="text-2xl font-bold text-gray-900">{queueStats.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Open Conflicts</p>
                <p className="text-2xl font-bold text-gray-900">{conflicts.filter(c => c.status === 'open').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last 24h Syncs</p>
                <p className="text-2xl font-bold text-gray-900">{sessions.filter(s => new Date(s.startedAt) > new Date(Date.now() - 86400000)).length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="space-y-4">
          {isLoadingSessions ? (
            <div className="text-center py-8">Loading sessions...</div>
          ) : (
            sessions.map((session) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(session.status)}
                      <h3 className="text-lg font-medium text-gray-900">Session {session.id}</h3>
                    </div>
                    <p className="text-sm text-gray-500">
                      {session.type} • {session.connectorId} • {new Date(session.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex space-x-4 text-sm">
                      <span>Processed: {session.entitiesProcessed}</span>
                      <span>Created: {session.entitiesCreated}</span>
                      <span>Updated: {session.entitiesUpdated}</span>
                      <span>Skipped: {session.entitiesSkipped}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => console.log('Session details', session.id)}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                </div>
                {session.errors.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 rounded text-sm text-red-700">
                    {session.errors.map((error, i) => (
                      <div key={i}>{error.message}</div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}

      {activeTab === 'conflicts' && (
        <div className="space-y-4">
          {isLoadingConflicts ? (
            <div className="text-center py-8">Loading conflicts...</div>
          ) : (
            conflicts.map((conflict) => (
              <motion.div
                key={conflict.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(conflict.severity)}`}>
                        {conflict.severity}
                      </span>
                      <h3 className="text-lg font-medium text-gray-900">
                        {conflict.entityType} • {conflict.entityId}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500">
                      Detected {new Date(conflict.detectedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm">
                      Resolve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedConflict(conflict)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Pending</h3>
            <p className="text-3xl font-bold text-blue-600">{queueStats.pending}</p>
            <p className="text-sm text-gray-500">Awaiting processing</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Retrying</h3>
            <p className="text-3xl font-bold text-orange-600">{queueStats.retrying}</p>
            <p className="text-sm text-gray-500">Will retry automatically</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed</h3>
            <p className="text-3xl font-bold text-red-600">{queueStats.failed}</p>
            <p className="text-sm text-gray-500">Requires manual intervention</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Completed</h3>
            <p className="text-3xl font-bold text-green-600">{queueStats.completed}</p>
            <p className="text-sm text-gray-500">Successfully processed</p>
          </div>
        </div>
      )}

      {/* Conflict Resolution Modal */}
      {selectedConflict && (
        <ConflictResolutionModal
          conflict={selectedConflict}
          onResolve={handleResolveConflict}
          onClose={() => setSelectedConflict(null)}
        />
      )}
    </div>
  );
};

export default SyncIntelligencePage;
